import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import { toast } from '../../../../../ui/toast';
import employeeIllustration from '../../../../../assets/images/backgrounds/employee_performance.png';
import candidateIllustration from '../../../../../assets/images/backgrounds/job_candidate.png';
import partnerIllustration from '../../../../../assets/images/backgrounds/partner_performance.png';
import { Button } from '../../../../../ui';
import { CardIcon } from '../../../../../ui/icons/icons';
import {
  addSubscription,
  cancelSubscription,
  getStripePortalLink,
  getSubscriptionProducts,
} from '../../../../../utils/api_service/subscription_api';
import { FeatureNames } from '../../../../../utils/helpers/subscriptionEnum';
import { fetchSubscriptions } from '../../../../../utils/redux_store/features/subscription_reducer';
import { RootState, store } from '../../../../../utils/redux_store/store';
import { Product, LicenseDateRange } from '../../../../../utils/types/admin_types';
import { getCredits } from '../../../../../utils/api_service/credits_api';
import { UsageMeter } from '../../../../../ui/usage_meter/UsageMeter';
import {
  CANDI_BANK_CV,
  FeatureCredits,
  creditTone,
  formatResetDate,
} from '../../../../../utils/types/credits_types';
import { FeatureCard } from './FeatureCard';

const LICENSE_CREDIT_FEATURE: Partial<Record<string, string>> = {
  hierTalentCandiBank: CANDI_BANK_CV,
};

const featureDescriptions = {
  candidateDescription:
    'Automatically assess any skills of any candidate at any time. Easily compare candidate results and automatically short list the top contenders.',
  employeeDescription:
    'Continuously assess employees to keep their skills sharp. Compare employees within teams, and compare the skills of teams and departments. Identify high and low achievers, as well as skill gaps that needs attention.',
  partnerDescription:
    'Ensure you select only the best contractors to work for you. Assess all potential partners and compare employees between different partners in order to make the best fully informed decision.',
};

export const Subscription: React.FC = () => {
  const subscription = useSelector(
    (state: RootState) => state.subscriptions.subscriptions,
  );

  const [productsList, setProductsList] = useState<Product[]>();
  const [credits, setCredits] = useState<Record<string, FeatureCredits>>({});
  const [creditsError, setCreditsError] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(false);

  const hierTalentProducts = [
    {
      key: 'hierTalentBase' as const,
      label: 'HierTalent Base',
      features: ['Applicant Analysis'],
      color: 'emerald',
    },
    {
      key: 'hierTalentAutoClassify' as const,
      label: 'HierTalent Auto Classify',
      features: ['Auto Suitability', 'Auto Outstanding'],
      color: 'indigo',
    },
    {
      key: 'hierTalentJobPost' as const,
      label: 'HierTalent Job Post',
      features: ['Job Posting'],
      color: 'amber',
    },
    {
      key: 'hierTalentAutoAssessments' as const,
      label: 'HierTalent Auto Assessments',
      features: ['Automated Assessment Management', 'Auto Scoring'],
      color: 'blue',
    },
    {
      key: 'hierTalentSmartInterviews' as const,
      label: 'HierTalent Smart Interviews',
      features: ['Recorded Interviews', 'AI Analysis & Scoring'],
      color: 'rose',
    },
    {
      key: 'hierTalentCandiBank' as const,
      label: 'HierTalent CandiBank',
      features: ['Candidate Bank Access'],
      color: 'slate',
    },
    {
      key: 'hierTalentCca' as const,
      label: 'HierTalent CCA',
      features: ['Candidate Competency Analysis'],
      color: 'violet',
    },
    {
      key: 'hierTalentPsychometricFaxtor' as const,
      label: 'HierTalent Psychometric Faxtor',
      features: ['Psychometric Analytics'],
      color: 'purple',
    },
  ];

  useEffect(() => {
    getSubscriptionProducts()
      .then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            setProductsList(data);
            setProductsLoading(false);
          });
        } else {
          setProductsError(true);
          setProductsLoading(false);
        }
      })
      .catch(() => {
        setProductsError(true);
        setProductsLoading(false);
      });
  }, []);

  useEffect(() => {
    getCredits()
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data === 'object') {
          setCredits(data);
        } else {
          setCreditsError(true);
        }
      })
      .catch(() => setCreditsError(true));
  }, []);

  const isLicenseActive = (
    license?:
      | LicenseDateRange
      | { status?: string; startDate?: number | string; endDate?: number | string },
  ): boolean => {
    if (!license) return false;
    if ('status' in license && license.status === 'active') return true;
    if (license.endDate) {
      const num = Number(license.endDate);
      if (!isNaN(num) && num > 0) {
        return num * 1000 > Date.now();
      }
      const parsed = Date.parse(String(license.endDate));
      if (!isNaN(parsed)) {
        return parsed > Date.now();
      }
    }
    return false;
  };

  const unixToDisplay = (unix?: number | string): string => {
    if (!unix) return '--';
    const num = Number(unix);
    const date = !isNaN(num) && num > 0 ? new Date(num * 1000) : new Date(unix);
    if (isNaN(date.getTime())) return '--';
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSubscriptionObject = (feature: string) => {
    if (subscription) {
      switch (feature) {
        case FeatureNames.CANDIDATE:
          return subscription.candidateFeature;
        case FeatureNames.EMPLOYEE:
          return subscription.employeeFeature;
        case FeatureNames.PARTNER:
          return subscription.partnerFeature;
      }
    }
  };

  const getTrialStatus = (feature: string) => {
    if (subscription) {
      switch (feature) {
        case FeatureNames.CANDIDATE:
          return subscription.trial.candidateFeature;
        case FeatureNames.EMPLOYEE:
          return subscription.trial.employeeFeature;
        case FeatureNames.PARTNER:
          return subscription.trial.partnerFeature;
        default:
          return true;
      }
    }
    return true;
  };

  const getImage = (feature: string) => {
    switch (feature) {
      case FeatureNames.CANDIDATE:
        return candidateIllustration;
      case FeatureNames.EMPLOYEE:
        return employeeIllustration;
      case FeatureNames.PARTNER:
        return partnerIllustration;
      default:
        return candidateIllustration;
    }
  };

  const getDescriptions = (feature: string) => {
    switch (feature) {
      case FeatureNames.CANDIDATE:
        return featureDescriptions.candidateDescription;
      case FeatureNames.EMPLOYEE:
        return featureDescriptions.employeeDescription;
      case FeatureNames.PARTNER:
        return featureDescriptions.partnerDescription;
      default:
        return featureDescriptions.candidateDescription;
    }
  };

  const createSubscription = async (priceId: string, trial: boolean) => {
    try {
      const response = await addSubscription(priceId, trial);
      if (response.ok) {
        toast.success(trial ? 'Trial Started' : 'Successfully subscribed', {
          position: 'bottom-center',
        });
        store.dispatch(fetchSubscriptions());
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error subscribing: ' + error, { position: 'bottom-center' });
    }
  };

  const deleteSubscription = async (featureType: string, cancel: boolean) => {
    try {
      const response = await cancelSubscription(featureType, cancel);
      if (response.ok) {
        toast.success(`${cancel ? 'Removed' : 'Renewed'} Subscription`, {
          position: 'bottom-center',
        });
        store.dispatch(fetchSubscriptions());
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error removing subscribing: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const accessStripePortal = async () => {
    try {
      const response = await getStripePortalLink();
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error accessing portal: ' + error, { position: 'bottom-center' });
    }
  };

  return (
    <Box sx={{ width: '100%', py: 2, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, width: '100%' }}>
        {/* Feature Cards Grid */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {productsLoading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              {[1, 2, 3].map((i) => (
                <Paper key={i} variant="outlined" sx={{ p: 3, borderRadius: '12px', minHeight: 400 }}>
                  <Skeleton variant="text" width="60%" height={28} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={160} sx={{ borderRadius: '8px', mb: 2 }} />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="40%" />
                </Paper>
              ))}
            </Box>
          ) : productsError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                Unable to load subscription plans
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>
                The products service is temporarily unavailable. Your active licenses are shown below.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
              {productsList?.map((product: Product) => (
                <FeatureCard
                  key={product.productId}
                  product={product}
                  companySize={subscription?.companySize || 'Small'}
                  interval={subscription?.interval || 'month'}
                  currentSubscription={getSubscriptionObject(product.feature)}
                  img={getImage(product.feature)}
                  description={getDescriptions(product.feature)}
                  isTrialUsed={getTrialStatus(product.feature)}
                  cardDetailsUnavailable={subscription?.defaultCard === undefined}
                  addSubscription={createSubscription}
                  cancelSubscription={deleteSubscription}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Payment Card */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: '12px',
              bgcolor: 'background.paper',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', mb: 2 }}>
              Payment Details
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              {subscription && subscription?.defaultCard ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CardIcon
                      strokeColor={
                        subscription?.defaultCard.brand === 'Visa' ? '#29AAE2' : '#D72A27'
                      }
                    />
                    <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {subscription?.defaultCard.brand}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', fontSize: '0.75rem', color: 'text.secondary' }}>
                    <Typography variant="caption">
                      Ending in <Box component="span" sx={{ fontWeight: 600 }}>{subscription?.defaultCard.last4}</Box>
                    </Typography>
                    <Typography variant="caption">
                      Expires <Box component="span" sx={{ fontWeight: 600 }}>{subscription?.defaultCard.expiryDate}</Box>
                    </Typography>
                  </Box>
                </>
              ) : (
                <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                  No card details available
                </Typography>
              )}
            </Box>
            <Button variant="primary" label="Manage" onClick={() => accessStripePortal()} />
          </Paper>

          {/* Policy Card */}
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: '12px',
              bgcolor: 'action.hover',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
              Policy
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 0.5 }}>
              <Box component="span" sx={{ fontWeight: 500 }}>Company Size:</Box>{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {subscription?.companySize ?? '--'}
              </Box>
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              <Box component="span" sx={{ fontWeight: 500 }}>Billing Cycle:</Box>{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', textTransform: 'capitalize' }}>
                {subscription?.interval
                  ? subscription.interval.charAt(0).toUpperCase() + subscription.interval.slice(1)
                  : '--'}
              </Box>
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* HierTalent Licenses */}
      <Box sx={{ mt: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 3, letterSpacing: '-0.02em' }}>
          Platform Licenses
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {hierTalentProducts.map(({ key, label, features }) => {
            const license = subscription?.[key];
            const active = isLicenseActive(license);

            return (
              <Paper
                key={key}
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  bgcolor: 'background.paper',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  opacity: active ? 1 : 0.6,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'action.active',
                  },
                }}
              >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: 'text.primary' }}>
                    {label}
                  </Typography>
                  <Chip
                    size="small"
                    label={active ? 'Active' : license ? 'Expired' : 'Not Licensed'}
                    color={active ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600 }}
                  />
                </Box>

                {/* Dates */}
                {license ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                        Start Date
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                        {unixToDisplay(license.startDate)}
                      </Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                        End Date
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: active ? 'text.primary' : 'error.main' }}>
                        {unixToDisplay(license.endDate)}
                      </Typography>
                    </Paper>
                  </Box>
                ) : (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
                      No license assigned
                    </Typography>
                  </Paper>
                )}

                {/* Monthly credits */}
                {license && LICENSE_CREDIT_FEATURE[key] && (() => {
                  const c = credits[LICENSE_CREDIT_FEATURE[key] as string];
                  const limit = license.monthlyCredits ?? null;
                  const tone = c ? creditTone(c) : 'ok';
                  return (
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                          Credits / month
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.primary' }}>
                          {limit === null ? 'Unlimited' : `${limit.toLocaleString()} ${c?.unit ?? 'CVs'}`}
                        </Typography>
                      </Box>
                      {c ? (
                        <UsageMeter
                          used={c.used}
                          limit={c.monthlyLimit}
                          tone={tone}
                          leftLabel={c.unlimited ? `${c.used.toLocaleString()} used this month` : `${c.used.toLocaleString()} used · ${(c.remaining ?? 0).toLocaleString()} left`}
                          rightLabel={`resets ${formatResetDate(c)}`}
                        />
                      ) : creditsError ? (
                        <Typography sx={{ fontSize: '0.6875rem', color: 'error.main', fontWeight: 500 }}>
                          Usage unavailable right now
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 500 }}>
                          Usage loading…
                        </Typography>
                      )}
                    </Paper>
                  );
                })()}

                {/* Features */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                    Includes
                  </Typography>
                  {features.map((feature) => (
                    <Stack key={feature} direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: active ? 'success.main' : 'text.disabled',
                        }}
                      />
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Note */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>
          NOTE: To change any policies, please contact{' '}
          <Link href="mailto:sales@hiacuity.com" sx={{ color: 'primary.main', fontWeight: 600 }}>
            sales@hiacuity.com
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};
